/**
 * Shared bookkeeping for stacked overlays (dialogs, sheets, fullscreen
 * overlays): only the top one reacts to Escape and traps Tab, popovers render
 * inside the top one, and body scroll stays locked until the last one closes.
 *
 * Order comes from the layer first (a blocking prompt such as redeem approval
 * stays on top of any ordinary dialog, even one that opens after it), then
 * from a sequence number taken when an overlay opens (during render, so a
 * parent always gets a lower number than a child it renders), not from effect
 * order (children's effects run first).
 */
export type OverlayLayer = 'dialog' | 'blocking';
type Entry = { id: string; seq: number; el: HTMLElement | null; rank: number };

const RANK: Record<OverlayLayer, number> = { dialog: 0, blocking: 1 };

const stack: Entry[] = [];
let seqCounter = 0;
let locks = 0;
let savedOverflow = '';

export function nextOverlaySeq(): number {
  seqCounter += 1;
  return seqCounter;
}

export function pushOverlay(
  id: string,
  seq: number = nextOverlaySeq(),
  el: HTMLElement | null = null,
  layer: OverlayLayer = 'dialog'
): void {
  popOverlay(id);
  stack.push({ id, seq, el, rank: RANK[layer] });
  stack.sort((a, b) => a.rank - b.rank || a.seq - b.seq);
}

export function popOverlay(id: string): void {
  const i = stack.findIndex((e) => e.id === id);
  if (i !== -1) stack.splice(i, 1);
}

export function isTopOverlay(id: string): boolean {
  return stack.length > 0 && stack[stack.length - 1].id === id;
}

/** Element of the top overlay, so popovers can render inside it (inside the modal). */
export function topOverlayElement(): HTMLElement | null {
  return stack.length > 0 ? stack[stack.length - 1].el : null;
}

export function lockScroll(): void {
  if (locks === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  locks += 1;
}

export function unlockScroll(): void {
  locks = Math.max(0, locks - 1);
  if (locks === 0) document.body.style.overflow = savedOverflow;
}
